/**
 * Product Card Slider
 * Handles image navigation in product cards
 */

class ProductCardSlider {
  constructor(element) {
    this.slider = element;
    this.track = element.querySelector('[data-slider-track]');
    this.prevBtn = element.querySelector('[data-slider-prev]');
    this.nextBtn = element.querySelector('[data-slider-next]');
    this.progress = element.querySelector('[data-slider-progress]');
    this.slides = element.querySelectorAll('.product-card-slider__slide');

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.hasDragged = false;
    this.dragTimeout = null;

    this.init();
  }

  init() {
    if (!this.track || this.totalSlides <= 1) return;

    // Button click handlers - use both click and pointerdown for reliability
    const handlePrevClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.hasDragged = true;
      this.prev();

      if (this.dragTimeout) {
        clearTimeout(this.dragTimeout);
      }
      this.dragTimeout = setTimeout(() => {
        this.hasDragged = false;
      }, 300);

      return false;
    };

    const handleNextClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.hasDragged = true;
      this.next();

      if (this.dragTimeout) {
        clearTimeout(this.dragTimeout);
      }
      this.dragTimeout = setTimeout(() => {
        this.hasDragged = false;
      }, 300);

      return false;
    };

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', handlePrevClick, { capture: true });
      this.prevBtn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
      }, { capture: true });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', handleNextClick, { capture: true });
      this.nextBtn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
      }, { capture: true });
    }

    // Touch/swipe support
    let touchStartX = 0;
    let touchEndX = 0;
    let isDragging = false;

    this.track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      isDragging = true;
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      touchEndX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.track.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
      isDragging = false;
    }, { passive: true });

    // Mouse drag support (desktop)
    this.mouseStartX = 0;
    this.mouseEndX = 0;
    this.isMouseDragging = false;

    // Bind handlers to use with document events
    this.handleMouseMove = (e) => {
      if (!this.isMouseDragging) return;
      this.mouseEndX = e.clientX;
      const diff = Math.abs(this.mouseStartX - this.mouseEndX);
      if (diff > 5) {
        this.hasDragged = true;
      }
    };

    this.handleMouseUp = (e) => {
      if (!this.isMouseDragging) return;
      this.mouseEndX = e.clientX;

      if (this.hasDragged) {
        this.handleSwipe(this.mouseStartX, this.mouseEndX);
        e.preventDefault();
        e.stopPropagation();

        if (this.dragTimeout) {
          clearTimeout(this.dragTimeout);
        }
        this.dragTimeout = setTimeout(() => {
          this.hasDragged = false;
        }, 300);
      }

      this.isMouseDragging = false;
      this.track.style.cursor = 'grab';

      // Remove document listeners
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    };

    this.track.addEventListener('mousedown', (e) => {
      this.mouseStartX = e.clientX;
      this.mouseEndX = e.clientX;
      this.isMouseDragging = true;
      this.hasDragged = false;
      this.track.style.cursor = 'grabbing';
      e.preventDefault();

      // Add document listeners for move and up (to catch events outside element)
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    });

    // Prevent all clicks on the slider container when dragged
    this.slider.addEventListener('click', (e) => {
      if (this.hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    }, { capture: true });

    // Also prevent clicks on the product card link (both old and new structure)
    const productLink = this.slider.closest('.product-card__link') || this.slider.querySelector('.product-card-slider__link');
    if (productLink) {
      productLink.addEventListener('click', (e) => {
        if (this.hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }, { capture: true });
    }

    // Set initial cursor
    this.track.style.cursor = 'grab';

    // Recalculate position on resize/orientation change
    this._resizeHandler = () => {
      if (this.currentIndex > 0) {
        this.track.style.transition = 'none';
        this.updateSlider();
        // Restore transition after reflow
        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      }
    };
    window.addEventListener('resize', this._resizeHandler);

    // Trackpad/wheel horizontal scroll support
    this.wheelTimeout = null;
    this.isWheelScrolling = false;
    this.accumulatedDeltaX = 0;

    this.slider.addEventListener('wheel', (e) => {
      // Check if it's horizontal scroll (trackpad gesture)
      const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);

      if (!isHorizontalScroll) return;

      // Always prevent default for horizontal scroll to avoid browser back/forward
      e.preventDefault();
      e.stopPropagation();

      // Don't navigate if debouncing
      if (this.isWheelScrolling) return;

      // Accumulate delta for smoother trackpad detection
      this.accumulatedDeltaX += e.deltaX;

      // Navigate when accumulated threshold is reached
      if (Math.abs(this.accumulatedDeltaX) > 30) {
        this.isWheelScrolling = true;
        this.hasDragged = true;

        if (this.accumulatedDeltaX > 0) {
          this.next();
        } else {
          this.prev();
        }

        // Reset accumulated delta
        this.accumulatedDeltaX = 0;

        // Reset debounce after delay
        if (this.wheelTimeout) {
          clearTimeout(this.wheelTimeout);
        }
        this.wheelTimeout = setTimeout(() => {
          this.isWheelScrolling = false;
          this.hasDragged = false;
        }, 300);
      }

      // Reset accumulated delta if no scroll for a while
      clearTimeout(this.accumulateTimeout);
      this.accumulateTimeout = setTimeout(() => {
        this.accumulatedDeltaX = 0;
      }, 100);
    }, { passive: false });
  }

  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) < threshold) return;

    if (diff > 0) {
      this.next();
    } else {
      this.prev();
    }
  }

  next() {
    if (this.currentIndex < this.totalSlides - 1) {
      this.currentIndex++;
      this.updateSlider();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSlider();
    }
  }

  updateSlider() {
    const slideWidth = this.slider.offsetWidth;
    this.track.style.transform = `translateX(${-this.currentIndex * slideWidth}px)`;

    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex === this.totalSlides - 1;
    }

    if (this.progress) {
      const progressWidth = ((this.currentIndex + 1) / this.totalSlides) * 100;
      this.progress.style.width = `${progressWidth}%`;
    }
  }
}

// Initialize all sliders when DOM is ready
// Exposed globally for use by other scripts (e.g., product-recommendations.js)
window.initProductCardSliders = function initProductCardSliders(container) {
  const root = container || document;
  const sliders = root.querySelectorAll('[data-slider]');
  sliders.forEach(slider => {
    if (slider.hasAttribute('data-slider-initialized')) {
      return;
    }
    // Mark as initialized immediately to prevent double init
    slider.setAttribute('data-slider-initialized', 'true');
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      new ProductCardSlider(slider);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.initProductCardSliders());
} else {
  window.initProductCardSliders();
}

// Re-initialize for Shopify section load events
document.addEventListener('shopify:section:load', () => window.initProductCardSliders());

// Watch for dynamically added content (infinite scroll, AJAX, etc.)
const sliderObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        // Check if the added node itself is a slider
        if (node.matches && node.matches('[data-slider]')) {
          window.initProductCardSliders(node.parentElement);
        }
        // Check if the added node contains sliders
        if (node.querySelectorAll) {
          const sliders = node.querySelectorAll('[data-slider]:not([data-slider-initialized])');
          if (sliders.length > 0) {
            window.initProductCardSliders(node);
          }
        }
      }
    });
  });
});

// Observe the entire body for changes
sliderObserver.observe(document.body, {
  childList: true,
  subtree: true
});
